import React, { useState, useRef, useEffect } from 'react';
import { 
  Palette, Droplets, Image, Users, Clock, BookOpen, Video,
  ChevronUp, ChevronDown, Plus, X, Save, Unlock, Crown,
  Upload, Trash2, AlertCircle, Bold, Italic, Underline, List, Type,
  File, Music, FileText, FileArchive, Folder, Edit2, Check, XCircle,
  CheckCircle, Headphones, Link
} from 'lucide-react';

const TrainingModaladmin = ({
  showTrainingModal,
  setShowTrainingModal,
  trainingFormData,
  setTrainingFormData,
  handleTrainingInputChange,
  handleTrainingTechniqueChange,
  addLessonToTraining,
  removeLessonFromTraining,
  moveLessonInTraining,
  editingTraining,
  loading,
  handleSaveTraining,
  allLessons,
  techniques,
  trainingCategories,
  levels,
  // Новые пропсы для аудиоуроков
  allAudioLessons = [], // Все доступные аудиоуроки
  addAudioLessonToTraining, // Функция добавления аудиоурока в тренинг
  removeAudioLessonFromTraining, // Функция удаления аудиоурока из тренинга
  moveAudioLessonInTraining // Функция перемещения аудиоурока
}) => {
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadErrors, setUploadErrors] = useState({});
  const [editingFileName, setEditingFileName] = useState(null);
  const [editedFileName, setEditedFileName] = useState('');
  const [imageError, setImageError] = useState(null);
  
  // Состояние для управления вкладками уроков
  const [activeLessonTab, setActiveLessonTab] = useState('video'); // 'video' или 'audio'
  
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const descriptionTextareaRef = useRef(null);
  
  const API_BASE_URL = 'https://artdownload2-production.up.railway.app';
  const folderName = 'training-files';
  const imageFolderName = 'training-images';

  // Устанавливаем статус "Черновик" по умолчанию при создании нового тренинга
  useEffect(() => {
    if (!editingTraining && trainingFormData && !trainingFormData.status) {
      setTrainingFormData(prev => ({
        ...prev,
        status: 'draft' // 'draft' - черновик, 'free' - бесплатный
      }));
    }
  }, [editingTraining, trainingFormData, setTrainingFormData]);

  // Поддерживаемые типы файлов
  const supportedFileTypes = {
    'application/pdf': { icon: <FileText className="w-5 h-5" />, color: 'text-red-500', label: 'PDF' },
    'audio/mpeg': { icon: <Music className="w-5 h-5" />, color: 'text-green-500', label: 'MP3' },
    'audio/wav': { icon: <Music className="w-5 h-5" />, color: 'text-green-500', label: 'WAV' },
    'audio/mp3': { icon: <Music className="w-5 h-5" />, color: 'text-green-500', label: 'MP3' },
    'image/jpeg': { icon: <Image className="w-5 h-5" />, color: 'text-blue-500', label: 'JPG' },
    'image/png': { icon: <Image className="w-5 h-5" />, color: 'text-blue-500', label: 'PNG' },
    'image/gif': { icon: <Image className="w-5 h-5" />, color: 'text-blue-500', label: 'GIF' },
    'application/zip': { icon: <FileArchive className="w-5 h-5" />, color: 'text-yellow-500', label: 'ZIP' },
    'application/x-rar-compressed': { icon: <FileArchive className="w-5 h-5" />, color: 'text-yellow-500', label: 'RAR' },
  };

  // Поддерживаемые типы изображений
  const supportedImageTypes = {
    'image/jpeg': { label: 'JPG' },
    'image/png': { label: 'PNG' },
    'image/gif': { label: 'GIF' },
    'image/webp': { label: 'WEBP' },
    'image/svg+xml': { label: 'SVG' },
  };

  // Получение иконки для типа файла
  const getFileIcon = (fileType) => {
    return supportedFileTypes[fileType] || { icon: <File className="w-5 h-5" />, color: 'text-gray-500', label: 'Файл' };
  };

  // Форматирование размера файла
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Функция для вставки форматирования в текст
  const insertFormatting = (formatType) => {
    const textarea = descriptionTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = trainingFormData.description?.substring(start, end) || '';
    let formattedText = '';
    let cursorOffset = 0;

    switch (formatType) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        cursorOffset = 2;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        cursorOffset = 1;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        cursorOffset = 3;
        break;
      case 'list':
        formattedText = selectedText
          .split('\n')
          .map(line => `• ${line}`)
          .join('\n');
        cursorOffset = 2;
        break;
      case 'paragraph':
        formattedText = `\n\n${selectedText}\n\n`;
        cursorOffset = 2;
        break;
      default:
        formattedText = selectedText;
    }

    const newDescription = 
      (trainingFormData.description || '').substring(0, start) + 
      formattedText + 
      (trainingFormData.description || '').substring(end);

    setTrainingFormData(prev => ({
      ...prev,
      description: newDescription
    }));

    setTimeout(() => {
      textarea.focus();
      if (selectedText) {
        textarea.setSelectionRange(
          start + (formattedText.length - selectedText.length) + cursorOffset,
          start + (formattedText.length - selectedText.length) + cursorOffset
        );
      } else {
        textarea.setSelectionRange(
          start + cursorOffset,
          start + cursorOffset
        );
      }
    }, 10);
  };

  // Функция для загрузки файла на сервер
  const uploadFileToServer = async (file, folder, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const xhr = new XMLHttpRequest();
    
    return new Promise((resolve, reject) => {
      xhr.open('POST', `${API_BASE_URL}/upload?folder=${folder}`);
      
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percentage = Math.round((event.loaded * 100) / event.total);
          onProgress({
            loaded: event.loaded,
            total: event.total,
            percentage
          });
        }
      };
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success && response.filename) {
              const fullUrl = `${API_BASE_URL}${response.url}`;
              resolve({
                ...response,
                url: fullUrl
              });
            } else {
              reject(new Error(response.error || 'Неизвестная ошибка сервера'));
            }
          } catch (error) {
            reject(new Error('Некорректный JSON ответ'));
          }
        } else {
          reject(new Error(`Ошибка загрузки: ${xhr.status} ${xhr.statusText}`));
        }
      };
      
      xhr.onerror = () => {
        reject(new Error('Ошибка сети при загрузке файла'));
      };
      
      xhr.send(formData);
    });
  };

  // Обработка выбора файлов
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    const errors = {};

    files.forEach(file => {
      const fileType = file.type;
      const maxSize = 50 * 1024 * 1024;
      
      if (file.size > maxSize) {
        errors[file.name] = `Файл слишком большой. Максимальный размер: 50MB`;
        return;
      }
      
      const isSupported = Object.keys(supportedFileTypes).includes(fileType) || 
                         fileType.startsWith('image/') || 
                         fileType.startsWith('audio/') ||
                         fileType === 'application/pdf' ||
                         fileType === 'application/zip' ||
                         fileType === 'application/x-rar-compressed';
      
      if (!isSupported) {
        errors[file.name] = `Формат не поддерживается`;
        return;
      }
      
      validFiles.push(file);
    });
    
    if (Object.keys(errors).length > 0) {
      const errorMessage = Object.entries(errors)
        .map(([name, error]) => `${name}: ${error}`)
        .join('\n');
      alert(`Некоторые файлы не прошли проверку:\n${errorMessage}`);
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
    
    event.target.value = '';
  };

  // Обработка выбора изображения
  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const file = files[0];
    const fileType = file.type;
    const maxSize = 10 * 1024 * 1024;
    
    if (!Object.keys(supportedImageTypes).includes(fileType)) {
      setImageError(`Формат изображения не поддерживается. Поддерживаемые форматы: ${Object.values(supportedImageTypes).map(i => i.label).join(', ')}`);
      event.target.value = '';
      return;
    }
    
    if (file.size > maxSize) {
      setImageError(`Изображение слишком большое. Максимальный размер: 10MB`);
      event.target.value = '';
      return;
    }

    setImageError(null);
    uploadImage(file);
    event.target.value = '';
  };

  // Загрузка изображения на сервер
  const uploadImage = async (file) => {
    setUploadingImage(true);
    
    try {
      const result = await uploadFileToServer(file, imageFolderName);
      setTrainingFormData(prev => ({
        ...prev,
        image: result.url
      }));
    } catch (error) {
      console.error('Ошибка загрузки изображения:', error);
      setImageError(`Ошибка загрузки изображения: ${error.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  // Удаление изображения тренинга
  const removeTrainingImage = () => {
    if (!window.confirm('Удалить изображение тренинга?')) return;
    setTrainingFormData(prev => ({ ...prev, image: '' }));
    setImageError(null);
  };

  // Удаление файла из списка выбранных
  const removeSelectedFile = (index) => {
    const fileName = selectedFiles[index].name;
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
    setUploadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fileName];
      return newErrors;
    });
  };

  // Загрузка файлов на сервер
  const uploadFiles = async () => {
    if (selectedFiles.length === 0) {
      alert('Выберите файлы для загрузки');
      return;
    }

    setUploadingFiles(true);
    setUploadErrors({});
    const uploadedFiles = [];
    const failedFiles = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const fileName = file.name;
      
      setUploadProgress(prev => ({
        ...prev,
        [fileName]: { loaded: 0, total: file.size, percentage: 0 }
      }));

      try {
        const result = await uploadFileToServer(
          file,
          folderName,
          (progress) => {
            setUploadProgress(prev => ({
              ...prev,
              [fileName]: progress
            }));
          }
        );
        
        uploadedFiles.push({
          name: result.originalName || file.name,
          filename: result.filename,
          type: result.mimeType || file.type,
          size: result.size || file.size,
          url: result.url,
          uploadedAt: new Date().toISOString()
        });
        
        setUploadProgress(prev => ({
          ...prev,
          [fileName]: { loaded: file.size, total: file.size, percentage: 100 }
        }));
        
      } catch (error) {
        console.error(`Ошибка загрузки файла ${fileName}:`, error);
        setUploadErrors(prev => ({
          ...prev,
          [fileName]: error.message
        }));
        failedFiles.push(fileName);
      }
    }

    if (uploadedFiles.length > 0) {
      setTrainingFormData(prev => ({
        ...prev,
        files: [...(prev.files || []), ...uploadedFiles]
      }));
    }

    const newSelectedFiles = selectedFiles.filter(file => 
      failedFiles.includes(file.name)
    );
    setSelectedFiles(newSelectedFiles);

    if (failedFiles.length > 0) {
      alert(`Не удалось загрузить: ${failedFiles.length} файл(ов)\nПроверьте консоль для подробностей`);
    }

    setUploadingFiles(false);
  };

  // Удаление загруженного файла
  const removeUploadedFile = async (fileIndex) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот файл из списка?')) return;

    try {
      const newFiles = trainingFormData.files.filter((_, index) => index !== fileIndex);
      setTrainingFormData(prev => ({ ...prev, files: newFiles }));
    } catch (error) {
      console.error('Ошибка удаления файла:', error);
      alert('Ошибка при удалении файла');
    }
  };

  // Начать редактирование имени файла
  const startEditFileName = (fileIndex) => {
    const file = trainingFormData.files[fileIndex];
    setEditingFileName(fileIndex);
    setEditedFileName(file.name || '');
  };

  // Сохранить отредактированное имя файла
  const saveEditedFileName = (fileIndex) => {
    if (!editedFileName.trim()) {
      alert('Название файла не может быть пустым');
      return;
    }

    const originalFile = trainingFormData.files[fileIndex];
    const originalName = originalFile.name || originalFile.filename || '';
    const extension = originalName.includes('.') 
      ? originalName.substring(originalName.lastIndexOf('.'))
      : '';
    
    let newFileName = editedFileName.trim();
    if (!newFileName.includes('.') && extension) {
      newFileName += extension;
    }

    const updatedFiles = [...trainingFormData.files];
    updatedFiles[fileIndex] = {
      ...updatedFiles[fileIndex],
      name: newFileName
    };

    setTrainingFormData(prev => ({
      ...prev,
      files: updatedFiles
    }));

    setEditingFileName(null);
    setEditedFileName('');
  };

  // Отменить редактирование имени файла
  const cancelEditFileName = () => {
    setEditingFileName(null);
    setEditedFileName('');
  };

  // Обработка нажатия Enter при редактировании
  const handleEditKeyPress = (e, fileIndex) => {
    if (e.key === 'Enter') {
      saveEditedFileName(fileIndex);
    } else if (e.key === 'Escape') {
      cancelEditFileName();
    }
  };

  // Очистка всех выбранных файлов
  const clearSelectedFiles = () => {
    setSelectedFiles([]);
    setUploadProgress({});
    setUploadErrors({});
  };

  // ============ ИСПРАВЛЕНИЕ ДЛЯ FIREBASE ============
  // Функция для подготовки данных перед отправкой в Firebase
  const prepareDataForFirebase = () => {
    // Создаем копию данных
    const dataToSend = { ...trainingFormData };
    
    // Если описание пустое или состоит только из пробелов - устанавливаем пустую строку
    // Firebase лучше работает с пустой строкой, чем с undefined или отсутствующим полем
    if (!dataToSend.description || dataToSend.description.trim() === '') {
      dataToSend.description = '';
    }
    
    // Убеждаемся, что все обязательные поля существуют
    if (!dataToSend.title) {
      throw new Error('Название обязательно для заполнения');
    }
    
    // Устанавливаем статус по умолчанию "Черновик", если он не указан
    if (!dataToSend.status) {
      dataToSend.status = 'draft';
    }
    
    return dataToSend;
  };

  // Проверка перед сохранением тренинга
  const handleSaveWithFiles = async () => {
    try {
      // Проверяем наличие незагруженных файлов
      if (selectedFiles.length > 0) {
        const shouldUpload = window.confirm(
          `У вас есть ${selectedFiles.length} незагруженных файлов. Хотите загрузить их перед сохранением тренинга?`
        );
        
        if (shouldUpload) {
          await uploadFiles();
        } else {
          const saveAnyway = window.confirm(
            'Файлы не будут загружены. Продолжить сохранение тренинга без них?'
          );
          if (!saveAnyway) return;
        }
      }
      
      // Проверяем ошибку загрузки изображения
      if (imageError) {
        const saveAnyway = window.confirm(
          'Есть ошибка загрузки изображения. Продолжить сохранение без изображения?'
        );
        if (!saveAnyway) return;
      }
      
      // Подготавливаем данные для Firebase
      const dataForFirebase = prepareDataForFirebase();
      
      // Вызываем родительскую функцию сохранения с подготовленными данными
      handleSaveTraining(dataForFirebase);
      
    } catch (error) {
      console.error('Ошибка при подготовке данных:', error);
      alert(error.message);
    }
  };

  // Проверяем статус тренинга
  const getStatusInfo = () => {
    const status = trainingFormData.status || 'draft';
    if (status === 'draft') {
      return { 
        icon: <BookOpen className="w-4 h-4 mr-2" />, 
        text: 'Черновик',
        description: 'Тренинг сохраняется как черновик'
      };
    } else {
      return { 
        icon: <Unlock className="w-4 h-4 mr-2" />, 
        text: 'Бесплатный',
        description: 'Тренинг доступен для всех пользователей'
      };
    }
  };

  // Обработка изменения статуса
  const handleStatusChange = (e) => {
    const newStatus = e.target.checked ? 'free' : 'draft';
    setTrainingFormData(prev => ({
      ...prev,
      status: newStatus
    }));
  };

  // Получение иконки для техники
  const getTechniqueIcon = (technique) => {
    switch (technique) {
      case 'Акрил':
        return <Palette className="inline w-4 h-4 mr-2" />;
      case 'Акварель':
        return <Droplets className="inline w-4 h-4 mr-2" />;
      case 'Видео':
        return <Video className="inline w-4 h-4 mr-2" />;
      default:
        return null;
    }
  };

  // Предпросмотр форматированного текста
  const renderFormattedPreview = (text) => {
    if (!text) return null;
    
    let formattedText = text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>')
      .replace(/• (.*?)(?:\n|$)/g, '<li>$1</li>')
      .replace(/\n\n/g, '</p><p>');
    
    if (!formattedText.startsWith('<p>')) {
      formattedText = `<p>${formattedText}</p>`;
    }
    
    formattedText = formattedText.replace(/<li>.*?<\/li>/g, (match) => {
      return `<ul>${match}</ul>`;
    });
    
    return <div dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };

  // Сброс состояния редактирования при закрытии модального окна
  useEffect(() => {
    if (!showTrainingModal) {
      setEditingFileName(null);
      setEditedFileName('');
      setSelectedFiles([]);
      setUploadProgress({});
      setUploadErrors({});
      setImageError(null);
      setActiveLessonTab('video');
    }
  }, [showTrainingModal]);

  return (
    showTrainingModal && (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4">
        <div className="bg-gradient-to-b from-gray-900 to-black rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-gradient-to-b from-gray-900 to-gray-900/95 p-4 flex items-center justify-between border-b border-gray-800">
            <h2 className="text-xl font-bold text-white">
              {editingTraining ? 'Редактировать составной урок' : 'Новый составной урок'}
            </h2>
            <button 
              onClick={() => setShowTrainingModal(false)}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-4 space-y-6">
            {/* Заголовок */}
            <div>
              <label className="block text-white mb-2">Название главного урока *</label>
              <input
                type="text"
                name="title"
                value={trainingFormData.title || ''}
                onChange={handleTrainingInputChange}
                className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Например: Основы медитативного рисования"
                required
              />
            </div>

            {/* Техника тренинга */}
            <div>
              <label className="block text-white mb-2">Техника урока</label>
              <div className="flex gap-2">
                {techniques.map(tech => (
                  <button
                    key={tech}
                    type="button"
                    onClick={() => handleTrainingTechniqueChange(tech)}
                    className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                      trainingFormData.technique === tech
                        ? tech === 'Акрил'
                          ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white'
                          : tech === 'Акварель'
                          ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white'
                          : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {getTechniqueIcon(tech)}
                    {tech}
                  </button>
                ))}
              </div>
            </div>

            {/* Описание с Rich Text Editor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-white">Описание</label>
                <span className="text-xs text-gray-400">Не обязательно</span>
              </div>
              
              {/* Панель инструментов */}
              <div className="flex items-center gap-1 p-2 bg-gray-800/70 rounded-t-lg border-b border-gray-700">
                <button
                  type="button"
                  onClick={() => insertFormatting('bold')}
                  className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
                  title="Жирный"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('italic')}
                  className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
                  title="Курсив"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('underline')}
                  className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
                  title="Подчеркивание"
                >
                  <Underline className="w-4 h-4" />
                </button>
                <div className="w-px h-6 bg-gray-700 mx-1"></div>
                <button
                  type="button"
                  onClick={() => insertFormatting('list')}
                  className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
                  title="Маркированный список"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('paragraph')}
                  className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
                  title="Абзац"
                >
                  <Type className="w-4 h-4" />
                </button>
              </div>
              
              <textarea
                ref={descriptionTextareaRef}
                name="description"
                value={trainingFormData.description || ''}
                onChange={handleTrainingInputChange}
                rows="6"
                className="w-full bg-gray-800 text-white rounded-b-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="Введите описание тренинга... Можно использовать форматирование через панель инструментов"
              />
              
              {trainingFormData.description && (
                <div className="mt-3 p-3 bg-gray-800/30 rounded-lg">
                  <p className="text-xs text-gray-400 mb-2">Предпросмотр:</p>
                  <div className="prose prose-invert max-w-none text-white/80">
                    {renderFormattedPreview(trainingFormData.description)}
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-400 mt-1">
                Используйте панель инструментов для форматирования текста. 
                **жирный**, *курсив*, &lt;u&gt;подчеркивание&lt;/u&gt;, • список
              </p>
            </div>

            {/* Изображение тренинга */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-white flex items-center">
                  <Image className="w-4 h-4 mr-2" />
                  Изображение тренинга
                </label>
                {trainingFormData.image && (
                  <button
                    type="button"
                    onClick={removeTrainingImage}
                    className="text-sm text-red-400 hover:text-red-300 flex items-center"
                    disabled={uploadingImage}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Удалить
                  </button>
                )}
              </div>
              
              {trainingFormData.image ? (
                <div className="mb-3">
                  <div className="relative w-full h-48 rounded-lg overflow-hidden mb-2">
                    <img 
                      src={trainingFormData.image} 
                      alt="Предпросмотр изображения тренинга"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1513366208864-87536b8bd7b4?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-3">
                      <span className="text-xs text-white bg-black/50 px-2 py-1 rounded">
                        Текущее изображение
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">
                    URL: <span className="text-gray-300 truncate block">{trainingFormData.image}</span>
                  </p>
                </div>
              ) : (
                <div className="mb-3 p-4 border-2 border-dashed border-gray-700 rounded-lg text-center">
                  <Image className="w-12 h-12 mx-auto mb-2 text-gray-500" />
                  <p className="text-gray-400 text-sm">Изображение не выбрано</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Будет использовано изображение по умолчанию
                  </p>
                </div>
              )}
              
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className={`flex-1 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors ${
                    uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={uploadingImage || loading || uploadingFiles}
                >
                  {uploadingImage ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Загрузка...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {trainingFormData.image ? 'Заменить изображение' : 'Загрузить изображение'}
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    const url = prompt('Введите URL изображения (оставьте пустым для изображения по умолчанию):', trainingFormData.image || '');
                    if (url !== null) {
                      setTrainingFormData(prev => ({ ...prev, image: url || '' }));
                      setImageError(null);
                    }
                  }}
                  className="px-4 py-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                  disabled={uploadingImage || loading || uploadingFiles}
                >
                  URL
                </button>
              </div>
              
              <input
                type="file"
                ref={imageInputRef}
                onChange={handleImageSelect}
                accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
                className="hidden"
                disabled={uploadingImage || loading || uploadingFiles}
              />
              
              {imageError && (
                <div className="mt-2 p-3 bg-red-900/30 border border-red-700/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <p className="text-sm text-red-400">{imageError}</p>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-400 mt-2">
                Поддерживаемые форматы: JPG, PNG, GIF, WEBP, SVG (максимум 10MB)
              </p>
            </div>

            {/* Инструктор и длительность */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  Инструктор
                </label>
                <input
                  type="text"
                  name="instructor"
                  value={trainingFormData.instructor || ''}
                  onChange={handleTrainingInputChange}
                  className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Имя инструктора"
                />
              </div>
              <div>
                <label className="block text-white mb-2 flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  Общая длительность
                </label>
                <input
                  type="text"
                  name="duration"
                  value={trainingFormData.duration || ''}
                  onChange={handleTrainingInputChange}
                  className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Например: 5 часов"
                />
              </div>
            </div>

            {/* Категория и уровень */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2">Категория</label>
                <select
                  name="category"
                  value={trainingFormData.category || ''}
                  onChange={handleTrainingInputChange}
                  className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {trainingCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-white mb-2">Уровень</label>
                <select
                  name="level"
                  value={trainingFormData.level || ''}
                  onChange={handleTrainingInputChange}
                  className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {levels.map(lvl => (
                    <option key={lvl} value={lvl}>{lvl}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Вкладки для выбора уроков */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-white font-medium">Уроки в тренинге</label>
                <span className="text-sm text-gray-400">
                  {(trainingFormData.lessons?.length || 0) + (trainingFormData.audioLessons?.length || 0)} уроков
                </span>
              </div>

              {/* Список добавленных уроков (видео и аудио) */}
              <div className="space-y-2 mb-4">
                {/* Видео уроки */}
                {(trainingFormData.lessons || []).map((lessonId, index) => {
                  const lesson = allLessons.find(l => l.id === lessonId);
                  return lesson ? (
                    <div key={`video-${lessonId}`} className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-sm text-gray-400 w-6">{index + 1}.</span>
                        <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0">
                          <img 
                            src={lesson.image} 
                            alt={lesson.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-white truncate block">
                            <Video className="w-3 h-3 inline mr-1 text-blue-400" />
                            {lesson.title}
                          </span>
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            {getTechniqueIcon(lesson.technique)}
                            <span>{lesson.technique}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveLessonInTraining(index, 'up')}
                          disabled={index === 0 || uploadingImage || loading || uploadingFiles}
                          className="p-1 text-gray-400 hover:text-white disabled:opacity-30"
                          title="Переместить вверх"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveLessonInTraining(index, 'down')}
                          disabled={index === (trainingFormData.lessons || []).length - 1 || uploadingImage || loading || uploadingFiles}
                          className="p-1 text-gray-400 hover:text-white disabled:opacity-30"
                          title="Переместить вниз"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeLessonFromTraining(lessonId)}
                          className="p-1 text-red-400 hover:text-red-300"
                          title="Удалить из тренинга"
                          disabled={uploadingImage || loading || uploadingFiles}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : null;
                })}

                {/* Аудио уроки */}
                {(trainingFormData.audioLessons || []).map((audioLessonId, index) => {
                  const audioLesson = allAudioLessons.find(a => a.id === audioLessonId);
                  // Корректируем индекс с учетом видеоуроков
                  const actualIndex = (trainingFormData.lessons?.length || 0) + index;
                  
                  return audioLesson ? (
                    <div key={`audio-${audioLessonId}`} className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg border-l-4 border-green-500">
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-sm text-gray-400 w-6">{actualIndex + 1}.</span>
                        <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-green-900/30 flex items-center justify-center">
                          <Headphones className="w-5 h-5 text-green-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-white truncate block">
                            <Music className="w-3 h-3 inline mr-1 text-green-400" />
                            {audioLesson.title}
                          </span>
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <Clock className="w-3 h-3" />
                            <span>{audioLesson.duration || 'Аудио'}</span>
                            {audioLesson.premium && (
                              <Crown className="w-3 h-3 ml-1 text-yellow-500" />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveAudioLessonInTraining(index, 'up')}
                          disabled={index === 0 || uploadingImage || loading || uploadingFiles}
                          className="p-1 text-gray-400 hover:text-white disabled:opacity-30"
                          title="Переместить вверх"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveAudioLessonInTraining(index, 'down')}
                          disabled={index === (trainingFormData.audioLessons || []).length - 1 || uploadingImage || loading || uploadingFiles}
                          className="p-1 text-gray-400 hover:text-white disabled:opacity-30"
                          title="Переместить вниз"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeAudioLessonFromTraining(audioLessonId)}
                          className="p-1 text-red-400 hover:text-red-300"
                          title="Удалить из тренинга"
                          disabled={uploadingImage || loading || uploadingFiles}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : null;
                })}

                {/* Если нет уроков */}
                {(trainingFormData.lessons?.length === 0 && trainingFormData.audioLessons?.length === 0) && (
                  <div className="p-6 bg-gray-800/30 rounded-lg text-center">
                    <BookOpen className="w-12 h-12 mx-auto mb-2 text-gray-600" />
                    <p className="text-gray-400">В тренинге пока нет уроков</p>
                    <p className="text-xs text-gray-500 mt-1">Добавьте видео или аудио уроки из списка ниже</p>
                  </div>
                )}
              </div>

              {/* Вкладки для выбора типа урока */}
              <div className="flex border-b border-gray-700 mb-4">
                <button
                  type="button"
                  onClick={() => setActiveLessonTab('video')}
                  className={`flex-1 py-2 px-4 text-sm font-medium transition-colors relative ${
                    activeLessonTab === 'video'
                      ? 'text-blue-400 border-b-2 border-blue-400'
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  <Video className="w-4 h-4 inline mr-2" />
                  Видео уроки
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLessonTab('audio')}
                  className={`flex-1 py-2 px-4 text-sm font-medium transition-colors relative ${
                    activeLessonTab === 'audio'
                      ? 'text-green-400 border-b-2 border-green-400'
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  <Music className="w-4 h-4 inline mr-2" />
                  Аудио уроки
                </button>
              </div>

              {/* Список доступных видео уроков */}
              {activeLessonTab === 'video' && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {allLessons
                    .filter(lesson => !(trainingFormData.lessons || []).includes(lesson.id))
                    .map(lesson => (
                      <div 
                        key={lesson.id} 
                        className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg hover:bg-gray-800/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0">
                            <img 
                              src={lesson.image} 
                              alt={lesson.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-white block">
                              {lesson.title}
                            </span>
                            <div className="flex items-center gap-1 text-xs text-gray-400">
                              {getTechniqueIcon(lesson.technique)}
                              <span>{lesson.technique}</span>
                              {lesson.premium && (
                                <Crown className="w-3 h-3 ml-1 text-yellow-500" />
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => addLessonToTraining(lesson.id)}
                          className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                          title="Добавить видео урок"
                          disabled={uploadingImage || loading || uploadingFiles}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  {allLessons.filter(lesson => !(trainingFormData.lessons || []).includes(lesson.id)).length === 0 && (
                    <p className="text-center text-gray-500 py-4">Все видео уроки уже добавлены</p>
                  )}
                </div>
              )}

              {/* Список доступных аудио уроков */}
              {activeLessonTab === 'audio' && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {allAudioLessons
                    .filter(audioLesson => !(trainingFormData.audioLessons || []).includes(audioLesson.id))
                    .map(audioLesson => (
                      <div 
                        key={audioLesson.id} 
                        className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg hover:bg-gray-800/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-green-900/30 flex items-center justify-center">
                            <Headphones className="w-5 h-5 text-green-400" />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-white block">
                              {audioLesson.title}
                            </span>
                            <div className="flex items-center gap-1 text-xs text-gray-400">
                              <Clock className="w-3 h-3" />
                              <span>{audioLesson.duration || 'Аудиоурок'}</span>
                              {audioLesson.premium && (
                                <Crown className="w-3 h-3 ml-1 text-yellow-500" />
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => addAudioLessonToTraining(audioLesson.id)}
                          className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                          title="Добавить аудио урок"
                          disabled={uploadingImage || loading || uploadingFiles}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  {allAudioLessons.length === 0 && (
                    <p className="text-center text-gray-500 py-4">Нет доступных аудио уроков</p>
                  )}
                  {allAudioLessons.length > 0 && allAudioLessons.filter(audioLesson => !(trainingFormData.audioLessons || []).includes(audioLesson.id)).length === 0 && (
                    <p className="text-center text-gray-500 py-4">Все аудио уроки уже добавлены</p>
                  )}
                </div>
              )}
            </div>

            {/* Раздел для загрузки файлов */}
            <div className="border border-gray-700 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center">
                    <Folder className="w-5 h-5 mr-2" />
                    Дополнительные материалы
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Загрузите PDF инструкции, аудио-медитации, дополнительные изображения
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedFiles.length > 0 && (
                    <button
                      type="button"
                      onClick={clearSelectedFiles}
                      className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                      disabled={uploadingFiles || uploadingImage}
                    >
                      Очистить
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    disabled={uploadingFiles || uploadingImage || loading}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Выбрать файлы
                  </button>
                </div>
              </div>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                accept=".pdf,.mp3,.wav,.jpg,.jpeg,.png,.gif,.zip,.rar"
                className="hidden"
                disabled={uploadingFiles || uploadingImage || loading}
              />

              {selectedFiles.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-white font-medium mb-2">
                    Выбрано файлов: {selectedFiles.length}
                    {uploadingFiles && ' (Идет загрузка...)'}
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                    {selectedFiles.map((file, index) => {
                      const fileIcon = getFileIcon(file.type);
                      const progress = uploadProgress[file.name];
                      const error = uploadErrors[file.name];
                      
                      return (
                        <div 
                          key={index} 
                          className={`p-3 rounded-lg transition-all ${
                            error 
                              ? 'bg-red-900/30 border border-red-700/50' 
                              : 'bg-gray-800/50 hover:bg-gray-800/70'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              <div className={`p-2 rounded-lg ${fileIcon.color} bg-gray-900`}>
                                {fileIcon.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-white truncate max-w-xs">
                                    {file.name}
                                  </p>
                                  {progress?.percentage === 100 && (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                  )}
                                </div>
                                <p className="text-xs text-gray-400">
                                  {formatFileSize(file.size)} • {fileIcon.label}
                                </p>
                                
                                {progress && progress.percentage < 100 && !error && (
                                  <div className="mt-2">
                                    <div className="w-full bg-gray-700 rounded-full h-1.5">
                                      <div 
                                        className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                                        style={{ width: `${progress.percentage}%` }}
                                      ></div>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">
                                      {progress.percentage}%
                                    </p>
                                  </div>
                                )}
                                
                                {error && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-400" />
                                    <p className="text-xs text-red-400">
                                      Ошибка: {error}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            {!uploadingFiles && !error && (
                              <button
                                type="button"
                                onClick={() => removeSelectedFile(index)}
                                className="p-1.5 text-red-400 hover:text-red-300 ml-2"
                                title="Удалить из списка"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {!uploadingFiles && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={uploadFiles}
                        disabled={selectedFiles.length === 0 || uploadingImage || loading}
                        className={`w-full py-3 rounded-lg font-medium flex items-center justify-center transition-all ${
                          selectedFiles.length === 0 || uploadingImage || loading
                            ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-600 to-emerald-500 text-white hover:opacity-90'
                        }`}
                      >
                        <Upload className="w-5 h-5 mr-2" />
                        Загрузить выбранные файлы ({selectedFiles.length})
                      </button>
                    </div>
                  )}
                </div>
              )}

              {trainingFormData.files && trainingFormData.files.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-white font-medium mb-2">
                    Загруженные материалы ({(trainingFormData.files || []).length})
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                    {trainingFormData.files.map((file, index) => {
                      const fileIcon = getFileIcon(file.type);
                      
                      return (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg hover:bg-gray-800/50 transition-colors group"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`p-2 rounded-lg ${fileIcon.color} bg-gray-900`}>
                              {fileIcon.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              {editingFileName === index ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={editedFileName}
                                    onChange={(e) => setEditedFileName(e.target.value)}
                                    onKeyDown={(e) => handleEditKeyPress(e, index)}
                                    className="flex-1 bg-gray-700 text-white rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => saveEditedFileName(index)}
                                    className="p-1 text-green-400 hover:text-green-300"
                                    title="Сохранить"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={cancelEditFileName}
                                    className="p-1 text-red-400 hover:text-red-300"
                                    title="Отменить"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <p className="text-sm font-medium text-white truncate max-w-xs">
                                    {file.name || file.filename}
                                  </p>
                                  <div className="flex flex-wrap items-center gap-2 mt-1">
                                    <span className="text-xs text-gray-400">
                                      {formatFileSize(file.size)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {fileIcon.label}
                                    </span>
                                    {file.uploadedAt && (
                                      <span className="text-xs text-gray-500">
                                        {new Date(file.uploadedAt).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {editingFileName !== index && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => startEditFileName(index)}
                                  className="p-1.5 text-yellow-400 hover:text-yellow-300"
                                  title="Редактировать название"
                                  disabled={uploadingFiles || uploadingImage || loading}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <a
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 text-blue-400 hover:text-blue-300"
                                  title="Открыть файл"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    fetch(file.url, { method: 'HEAD' })
                                      .catch(() => {
                                        alert('Файл недоступен по указанному URL');
                                        e.preventDefault();
                                      });
                                  }}
                                >
                                  <File className="w-4 h-4" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => removeUploadedFile(index)}
                                  className="p-1.5 text-red-400 hover:text-red-300"
                                  title="Удалить файл из списка"
                                  disabled={uploadingFiles || uploadingImage || loading}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-4 p-3 bg-gray-900/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-medium text-white">Поддерживаемые форматы:</h5>
                  <span className="text-xs text-gray-400">Макс. 50MB на файл</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(supportedFileTypes).map(([type, info]) => (
                    <div key={type} className="flex items-center gap-1">
                      <div className={info.color}>
                        {info.icon}
                      </div>
                      <span className="text-xs text-gray-400">{info.label}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Файлы сохраняются на отдельном сервере и доступны по прямым ссылкам
                </p>
              </div>
            </div>

            {/* Статус тренинга: Черновик/Бесплатный */}
            <div className="flex items-center p-3 bg-gray-800/30 rounded-lg">
              <input
                type="checkbox"
                id="trainingStatus"
                checked={trainingFormData.status === 'free'}
                onChange={handleStatusChange}
                className="w-5 h-5 rounded bg-gray-800 border-gray-700 text-green-500 focus:ring-green-500 focus:ring-offset-gray-900"
                disabled={uploadingImage || loading || uploadingFiles}
              />
              <label htmlFor="trainingStatus" className="ml-3 text-white flex items-center cursor-pointer">
                {getStatusInfo().icon}
                <span className="font-medium">{getStatusInfo().text}</span>
                <span className="ml-2 text-xs text-gray-400">({getStatusInfo().description})</span>
              </label>
            </div>
          </div>

          <div className="sticky bottom-0 bg-gradient-to-t from-gray-900 to-gray-900/95 p-4 border-t border-gray-800">
            <div className="flex gap-3">
              <button
                onClick={() => setShowTrainingModal(false)}
                className="flex-1 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
                disabled={loading || uploadingImage || uploadingFiles}
              >
                Отмена
              </button>
              <button
                onClick={handleSaveWithFiles}
                disabled={loading || uploadingImage || uploadingFiles}
                className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading || uploadingImage || uploadingFiles ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {uploadingFiles ? 'Загрузка файлов...' : uploadingImage ? 'Загрузка изображения...' : 'Сохранение...'}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    {editingTraining ? 'Обновить тренинг' : 'Создать тренинг'}
                  </>
                )}
              </button>
            </div>
            {(selectedFiles.length > 0 || uploadingFiles || uploadingImage || imageError) && (
              <p className="text-xs mt-2 text-center">
                {uploadingFiles 
                  ? '⏳ Идет загрузка файлов...' 
                  : uploadingImage
                  ? '⏳ Идет загрузка изображения...'
                  : imageError
                  ? <span className="text-red-400">⚠ Есть ошибка загрузки изображения</span>
                  : `📁 Есть ${selectedFiles.length} незагруженных файлов`}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  );
};

export default TrainingModaladmin;